// The single source of scoring truth.
// Every criterion belongs to exactly one strand, and Judgement criteria are humanOnly.
// If you change weights here, change docs/DATA_MODEL.md and recalibrate faculty.

export const STRANDS = {
  craft: { label: "Craft", weight: 0.3, floor: 70 },
  judgement: { label: "Judgement", weight: 0.3, floor: 70 },
  rigor: { label: "Rigor", weight: 0.25, floor: 70 },
  practice: { label: "Practice", weight: 0.15, floor: 70 },
};

export const SCALE = [
  { v: 1, label: "Inadequate" },
  { v: 2, label: "Developing" },
  { v: 3, label: "Proficient — a competent junior would ship this" },
  { v: 4, label: "Strong" },
  { v: 5, label: "Exemplary" },
];

// Present on every brief.
export const UNIVERSAL = [
  { id: "direction", label: "Direction quality", strand: "judgement", humanOnly: true,
    anchor3: "Context pack exists and was used; some machine-default output survives." },
  { id: "selection", label: "Selection quality", strand: "judgement", humanOnly: true,
    anchor3: "Choices made and explained, but after the fact rather than against a stated criterion." },
  { id: "verification", label: "Verification", strand: "rigor", humanOnly: false,
    anchor3: "Ran the automated checks; no evidence of an independent hand pass." },
  { id: "provenance", label: "Provenance", strand: "practice", humanOnly: false,
    anchor3: "Ledger complete but generic; the machine-failure field is thin." },
  { id: "defensibility", label: "Defensibility", strand: "judgement", humanOnly: true,
    anchor3: "Explains the decision without anticipating the strongest objection." },
];

// Brief-specific criteria. Kept short on purpose: a rubric a Studio Master cannot
// hold in their head produces drift, not precision.
export const SPECIFIC = {
  A00: [["personalisation","Agent personalisation","judgement"],["handbuild","Hand-build fidelity","craft"],["reflection","Reflection honesty","practice"]],
  A01: [["specificity","Specificity of teardown","judgement"],["principle","Principle citation","judgement"],["machinecrit","Critique of the machine","judgement"]],
  A02: [["tells","Quality of stated tells","judgement"],["prohibitions","Prohibitions specific to this brand","craft"],["negspace","Negative space","judgement"]],
  A03: [["scale","Scale integrity","craft"],["contrast","Contrast evidence","rigor"],["criterion","Criterion stated before generation","judgement"],["grayscale","Grayscale survival","craft"]],
  A04: [["delta","Two-briefs delta demonstrated","judgement"],["craftq","Homepage craft","craft"],["client","Client handling","practice"]],
  A05: [["guide","Guide quality","rigor"],["recruit","Recruitment honesty","rigor"],["listening","Listening beyond the script","rigor"],["disconfirm","Disconfirming evidence surfaced","rigor"]],
  A06: [["diagnosis","Specificity of the diagnosis","rigor"],["refusal","Legitimate-use and refusal reasoning","practice"]],
  A07: [["candidates","Candidate diversity","craft"],["testdesign","Test construction","rigor"],["datahonesty","Data honesty","rigor"]],
  A08: [["voice","Voice consistency","craft"],["states","Error and empty states written","craft"],["conduct","Test conduct","rigor"],["iteration","Iteration traces to an observation","rigor"]],
  A09: [["governance","Governance literacy","judgement"],["naming","Naming analysis","craft"]],
  A10: [["layers","Layer discipline","craft"],["semnaming","Semantic naming","craft"],["pipeline","Pipeline works end to end","craft"],["theme","Theme proof","craft"]],
  A11: [["spec","Spec before build","judgement"],["api","API sanity","craft"],["statematrix","State completeness","craft"],["corrections","Correction log","judgement"]],
  A12: [["docs","Documentation for a cold reader","craft"],["a11ynotes","Per-component accessibility notes","rigor"],["motion","Motion guidance","craft"]],
  A13: [["manual","Manual audit depth","rigor"],["toolmiss","Tool-miss analysis","rigor"],["sc","Criterion accuracy","rigor"],["severity","Severity judgement","judgement"]],
  A14: [["correctness","Fixes address the barrier","rigor"],["aria","ARIA discipline","rigor"],["twotwo","WCAG 2.2 additions handled","rigor"],["integrity","Design integrity preserved","craft"]],
  A15: [["budget","Budget set in advance","rigor"],["conditions","Field conditions honest","rigor"],["inp","INP handled","rigor"],["tradeoffs","Trade-offs argued","judgement"]],
  A16: [["sessionconduct","Session conduct","practice"],["honesty","Findings honesty","rigor"],["retest","Fix and re-test evidence","rigor"]],
  A17: [["catch","Catch rate","rigor"],["precision","False positive rate","rigor"],["sev","Severity judgement","judgement"],["automation","Automation thinking","craft"]],
  A18: [["semantics","Semantic quality","craft"],["commits","Commit discipline","practice"],["overrides","Correction evidence","judgement"],["deps","Dependency hygiene","rigor"],["readme","README completeness","craft"]],
  A19: [["structured","Structured data validity","craft"],["entity","Entity clarity","craft"],["prediction","Reasoned summary prediction","judgement"]],
  A20: [["metrics","Metric alignment","rigor"],["security","Security hygiene","rigor"],["datanote","Data note accuracy","practice"]],
  A21: [["scope","Scope precision","practice"],["commercial","Commercial judgement","practice"],["aiclause","AI clause quality","practice"],["negotiation","Negotiation conduct","practice"]],
  A22: [["transfer","Method transfer under pressure","judgement"],["recovery","Recovery from scope change","practice"],["ledgercurrency","Ledger kept during, not after","practice"]],
  A23: [["argument","Argument over narration","judgement"],["evidence","Evidence for claimed outcomes","rigor"],["failure","Failure case study honesty","practice"]],
  A24: [["problem","States the problem unaided","judgement"],["justify","Justifies three decisions","judgement"],["machinefail","Knows where the AI failed","judgement"],["challenge","Responds to challenge with reasoning","judgement"]],
};

export function criteriaFor(briefId) {
  const specific = (SPECIFIC[briefId] || []).map(([id, label, strand]) => ({
    id, label, strand, humanOnly: strand === "judgement",
  }));
  return [...UNIVERSAL, ...specific];
}

export function computeMark(briefId, scores) {
  const criteria = criteriaFor(briefId);
  const byStrand = {};
  for (const c of criteria) {
    const v = scores?.[c.id];
    if (!v) continue;
    (byStrand[c.strand] ||= []).push(v);
  }
  const strandPct = {};
  let total = 0, usedWeight = 0;
  for (const key of Object.keys(STRANDS)) {
    const vals = byStrand[key];
    if (!vals || !vals.length) continue;
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const pct = (mean / 5) * 100;
    strandPct[key] = Math.round(pct * 10) / 10;
    total += pct * STRANDS[key].weight;
    usedWeight += STRANDS[key].weight;
  }
  const overall = usedWeight ? Math.round((total / usedWeight) * 10) / 10 : 0;
  const belowFloor = Object.entries(strandPct)
    .filter(([k, v]) => v < STRANDS[k].floor)
    .map(([k]) => k);
  const complete = criteria.every((c) => scores?.[c.id]);
  return { overall, strandPct, belowFloor, complete, criteriaCount: criteria.length };
}

export const GATES = {
  "M1-G": { name: "Contrast", brief: "A04", resubmissions: null },
  "M2-G": { name: "Traceability", brief: "A08", resubmissions: 2 },
  "M3-G": { name: "Stranger Test", brief: "A12", resubmissions: 2 },
  "M4-G": { name: "Manual A11y", brief: "A16", resubmissions: 2 },
  "M5-G": { name: "Cold Deploy", brief: "A20", resubmissions: 1 },
  "M6-G": { name: "Defence", brief: "A24", resubmissions: 1 },
};
